import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudioPage.css';
import UniversalLayout from './components/UniversalLayout';
import { useAuthContext } from './components/AuthProvider';
import AuthModal from './components/AuthModal';
import { initializeProject, startAgentCompose } from './lib/api';

const STYLE_PRESETS = [
  {
    id: 'apple',
    name: 'Apple Keynote',
    desc: 'Clean key lighting, white background',
    prompt: 'Apple-style keynote presentation look: clean pure white background with no visible objects, soft key lighting from the front-left, subtle rim light separating the subject from the backdrop, shallow depth of field, neutral color grading with slight cool tones, minimal shadows, polished corporate aesthetic. No props — just the speaker sitting in front of a seamless white background.',
  },
  {
    id: 'youtube',
    name: 'Creator Setup',
    desc: 'Bright, balanced, ring light',
    prompt: 'YouTube creator studio setup: bright even lighting from a ring light, background features a styled desk with a monitor, small plants, LED strip lights casting soft color accents, floating shelves with books and collectibles, balanced warm-neutral color grading, slight bokeh on background elements, energetic and approachable feel.',
  },
  {
    id: 'podcast',
    name: 'Podcast Warm',
    desc: 'Soft warm tones, natural feel',
    prompt: 'Podcast studio atmosphere: warm soft lighting with amber tones, background shows acoustic foam panels on the walls, a professional microphone on a boom arm in front of the speaker, over-ear headphones, a small mixing desk or audio interface on the table, maybe a neon sign or warm lamp in the background, shallow depth of field with bokeh, slight film grain, natural skin tones with warm color grading, moody but inviting ambiance.',
  },
  {
    id: 'custom',
    name: 'Custom',
    desc: 'Describe your own style',
    prompt: '',
  },
];

function StudioPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [recordingOpen, setRecordingOpen] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('apple');
  const [customStylePrompt, setCustomStylePrompt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const {
    user, loading, openAuthModal, closeAuthModal, authModalOpen,
    signInWithGoogle, signInWithEmail,
  } = useAuthContext();
  const pendingSubmitRef = useRef(null);

  const handleRecordClick = () => {
    setRecordingOpen(true);
  };

  const handleRecordingClose = () => {
    setRecordingOpen(false);
  };

  const handleRecordingDone = useCallback((blob) => {
    setRecordedBlob(blob);
    setRecordedUrl(URL.createObjectURL(blob));
    setRecordingOpen(false);
  }, []);

  const handleDiscardRecording = () => {
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedBlob(null);
    setRecordedUrl(null);
  };

  const submitToAgent = useCallback(async (videoBlob, prompt) => {
    const ext = (videoBlob.type || 'video/webm').split('/')[1]?.split(';')[0] || 'webm';
    const file = videoBlob instanceof File
      ? videoBlob
      : new File([videoBlob], `studio-recording.${ext}`, { type: videoBlob.type || 'video/webm' });

    const projectId = Math.random().toString(36).substring(2, 15);

    await initializeProject(projectId, prompt, '16:9');
    await startAgentCompose(projectId, prompt, [file], 'pro', [], 'studio_talking_head_template');

    navigate(`/project/${projectId}`, {
      state: { agentMode: true, promptText: prompt },
    });
  }, [navigate]);

  useEffect(() => {
    if (user && pendingSubmitRef.current) {
      const { blob, prompt } = pendingSubmitRef.current;
      pendingSubmitRef.current = null;
      setUploading(true);
      setSubmitError(null);
      submitToAgent(blob, prompt).catch((err) => {
        console.error('Failed to submit video:', err);
        setSubmitError('Failed to submit. Please try again.');
        setUploading(false);
      });
    }
  }, [user, submitToAgent]);

  const handleUseRecording = async () => {
    if (!recordedBlob || uploading) return;

    const stylePrompt = selectedStyle === 'custom'
      ? customStylePrompt.trim()
      : (STYLE_PRESETS.find((p) => p.id === selectedStyle)?.prompt || '');
    const prompt = `Turn this video into a polished talking head video. Use the talking-head-video skill. Studio style: ${stylePrompt}`;

    if (!user && !loading) {
      pendingSubmitRef.current = { blob: recordedBlob, prompt };
      openAuthModal();
      return;
    }

    setUploading(true);
    setSubmitError(null);

    try {
      await submitToAgent(recordedBlob, prompt);
    } catch (err) {
      console.error('Failed to submit video:', err);
      setSubmitError('Failed to submit. Please try again.');
      setUploading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setRecordedBlob(file);
    setRecordedUrl(URL.createObjectURL(file));
    e.target.value = '';
  };

  return (
    <UniversalLayout>
      <div className="studio-content">
        <main className="studio-main">
          <h1 className="studio-title">
            Turn Webcam Videos Into{' '}
            <span className="studio-title-em">Studio-Grade</span>{' '}
            Launch Videos
          </h1>

          <p className="studio-subtitle">
            Upload any talking-head video and let AI transform it with cinematic
            lighting, shallow depth of field, and professional color grading —
            in minutes, not hours.
          </p>

          <div className="studio-upload-panel">
            {recordedUrl ? (
              <div className="studio-recorded-preview">
                <video
                  src={recordedUrl}
                  controls
                  className="studio-recorded-video"
                />
                <div className="studio-recorded-actions">
                  <button
                    className="studio-btn-record"
                    onClick={handleDiscardRecording}
                  >
                    Discard
                  </button>
                  <button
                    className="studio-btn-upload"
                    onClick={handleUseRecording}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="studio-btn-spinner" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        Use This Video
                      </>
                    )}
                  </button>
                  {submitError && (
                    <span className="studio-submit-error">{submitError}</span>
                  )}
                </div>
              </div>
            ) : (
              <div className="studio-upload-zone">
                <div className="studio-upload-text">
                  <span className="studio-upload-label">
                    Drop a video here or choose an option below
                  </span>
                  <span className="studio-upload-hint">
                    Your video stays private and is deleted after processing
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <div className="studio-upload-actions">
                  <button className="studio-btn-upload" onClick={handleUploadClick}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Upload Video
                  </button>
                  <button className="studio-btn-record" onClick={handleRecordClick}>
                    <span className="studio-record-dot" />
                    Record
                  </button>
                </div>
                <span className="studio-upload-formats">
                  MP4, MOV, WebM · Free to try
                </span>
              </div>
            )}
            <div className="studio-panel-divider" />
            <div className="studio-panel-presets">
              <span className="studio-panel-presets-label">Style Presets</span>
              <div className="studio-presets-grid">
                {STYLE_PRESETS.map((preset) => (
                  <PresetCard
                    key={preset.id}
                    swatch={preset.id}
                    name={preset.name}
                    desc={preset.desc}
                    selected={selectedStyle === preset.id}
                    onSelect={() => setSelectedStyle(preset.id)}
                  />
                ))}
              </div>
              {selectedStyle === 'custom' && (
                <textarea
                  className="studio-custom-prompt"
                  placeholder="Describe the studio style you want — e.g. A startup office with light furniture, glass walls and computer monitors."
                  value={customStylePrompt}
                  onChange={(e) => setCustomStylePrompt(e.target.value)}
                  rows={3}
                  autoFocus
                />
              )}
            </div>
          </div>
        </main>

        <section className="studio-how">
          <h2 className="studio-how-title">
            How it <em>works</em>
          </h2>
          <div className="studio-steps">
            <div className="studio-step">
              <div className="studio-step-number">01</div>
              <div className="studio-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <h3 className="studio-step-title">Upload</h3>
              <p className="studio-step-desc">
                Drop your webcam video or record directly in the browser.
              </p>
            </div>
            <div className="studio-step">
              <div className="studio-step-number">02</div>
              <div className="studio-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </div>
              <h3 className="studio-step-title">Choose Style</h3>
              <p className="studio-step-desc">
                Pick a studio preset or let AI select the best match.
              </p>
            </div>
            <div className="studio-step">
              <div className="studio-step-number">03</div>
              <div className="studio-step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h3 className="studio-step-title">Get Notified</h3>
              <p className="studio-step-desc">
                Watch the agent work in real time. Get an email when your video is ready.
              </p>
            </div>
          </div>
        </section>

        <footer className="studio-footer">
          Powered by Latted
        </footer>
      </div>

      {recordingOpen && (
        <RecordingModal
          onClose={handleRecordingClose}
          onDone={handleRecordingDone}
        />
      )}

      <AuthModal
        isOpen={!loading && !user && authModalOpen}
        onClose={closeAuthModal}
        onSignIn={() => signInWithGoogle()}
        onEmailSignIn={signInWithEmail}
      />
    </UniversalLayout>
  );
}

function RecordingModal({ onClose, onDone }) {
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [isRecording, setIsRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [facingMode, setFacingMode] = useState('user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [error, setError] = useState(null);
  const [reviewBlob, setReviewBlob] = useState(null);
  const [reviewUrl, setReviewUrl] = useState(null);

  const startCamera = useCallback(async (facing) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
    }
    setCameraReady(false);
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setCameraReady(true);
    } catch (err) {
      console.error('Camera access failed:', err);
      setError('Could not access camera. Please allow camera permissions and try again.');
    }
  }, []);

  useEffect(() => {
    (async () => {
      await startCamera(facingMode);
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoInputs = devices.filter((d) => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);
      } catch {
        // ignore
      }
    })();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFlipCamera = async () => {
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    await startCamera(next);
  };

  const handleStartRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];

    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : 'video/webm';

    const recorder = new MediaRecorder(streamRef.current, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      // Stop camera stream — no longer needed once recording is captured
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setReviewBlob(blob);
      setReviewUrl(url);
    };

    recorder.start(1000);
    setIsRecording(true);
    setElapsed(0);

    timerRef.current = setInterval(() => {
      setElapsed((prev) => {
        if (prev >= 299) {
          handleStopRecording();
          return 300;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const handleStopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const handleRetake = async () => {
    if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    setReviewBlob(null);
    setReviewUrl(null);
    setElapsed(0);
    await startCamera(facingMode);
  };

  const handleUseTake = () => {
    if (reviewBlob) {
      // Stream already stopped in recorder.onstop, but guard just in case
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      onDone(reviewBlob);
    }
  };

  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    // If user has a reviewed recording, treat close as "use this take"
    // so the recording isn't silently discarded
    if (reviewBlob) {
      onDone(reviewBlob);
    } else {
      onClose();
    }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (reviewUrl) {
    return (
      <div className="studio-rec-backdrop" onClick={handleClose}>
        <div className="studio-rec-modal" onClick={(e) => e.stopPropagation()}>
          <div className="studio-rec-header">
            <span className="studio-rec-title">Review Recording</span>
            <button className="studio-rec-close" onClick={handleClose} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="studio-rec-viewport">
            <video src={reviewUrl} controls className="studio-rec-review-video" />
          </div>
          <div className="studio-rec-footer">
            <button className="studio-rec-btn studio-rec-btn--secondary" onClick={handleRetake}>
              Retake
            </button>
            <button className="studio-rec-btn studio-rec-btn--primary" onClick={handleUseTake}>
              Use This Video
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-rec-backdrop" onClick={handleClose}>
      <div className="studio-rec-modal" onClick={(e) => e.stopPropagation()}>
        <div className="studio-rec-header">
          <span className="studio-rec-title">Record Video</span>
          <button className="studio-rec-close" onClick={handleClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="studio-rec-viewport">
          {error ? (
            <div className="studio-rec-error">{error}</div>
          ) : (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={`studio-rec-live ${facingMode === 'user' ? 'studio-rec-live--mirrored' : ''}`}
              />
              {!cameraReady && (
                <div className="studio-rec-loading">Starting camera…</div>
              )}
            </>
          )}

          {isRecording && (
            <div className="studio-rec-indicator">
              <span className="studio-rec-indicator-dot" />
              <span className="studio-rec-indicator-time">{formatTime(elapsed)}</span>
            </div>
          )}
        </div>

        <div className="studio-rec-footer">
          {hasMultipleCameras && !isRecording && (
            <button className="studio-rec-btn studio-rec-btn--icon" onClick={handleFlipCamera} aria-label="Switch camera">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          )}

          {!isRecording ? (
            <button
              className="studio-rec-btn studio-rec-btn--record"
              onClick={handleStartRecording}
              disabled={!cameraReady}
            >
              <span className="studio-rec-btn-dot" />
              Start Recording
            </button>
          ) : (
            <button className="studio-rec-btn studio-rec-btn--stop" onClick={handleStopRecording}>
              <span className="studio-rec-btn-square" />
              Stop
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PresetCard({ swatch, name, desc, selected, onSelect }) {
  return (
    <div
      className={`studio-preset-card${selected ? ' studio-preset-card--selected' : ''}`}
      onClick={onSelect}
    >
      <div className={`studio-preset-swatch studio-preset-swatch--${swatch}`}>
        {selected && (
          <svg className="studio-preset-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <div className="studio-preset-info">
        <span className="studio-preset-name">{name}</span>
        <span className="studio-preset-desc">{desc}</span>
      </div>
    </div>
  );
}

export default StudioPage;
